import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { axiosGet } from '../utils/axiosGet';
import { getUserRoles } from '../utils/getUserRoles';
import { devError } from '../utils/devLog';

export const LoggedInUserContext = createContext();

export const LoggedInUserContextProvider = ({ children }) => {
  const [userDetails, setUserDetails] = useState(null);
  const [refreshUserDetails, setRefreshUserDetails] = useState(0);
  const [originalUserDetails, setOriginalUserDetails] = useState(null);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const hasExecuted = useRef(new Set());

  useEffect(() => {
    if (hasExecuted.current.has(refreshUserDetails)) {
      return;
    }

    hasExecuted.current.add(refreshUserDetails);

    axiosGet('jwt-get-user-details')
      .then(async (response) => {
        if (response && response?.status === 200) {
          if (response.data && Object.values(response.data).length > 0) {
            const responseUserDetails = response.data;
            if (responseUserDetails?.id) {
              const userRoles = await getUserRoles(responseUserDetails);
              setUserDetails({ ...responseUserDetails, roles: userRoles });
              setOriginalUserDetails({
                ...responseUserDetails,
                roles: userRoles,
              });
            }
          }
        }
      })
      .catch((error) => {
        devError('Error fetching user details:', error);
      })
      .finally(() => {
        setHasCheckedAuth(true);
      });
  }, [refreshUserDetails]);

  return (
    <LoggedInUserContext.Provider
      value={useMemo(
        () => ({
          refreshUserDetails,
          setRefreshUserDetails,
          originalUserDetails,
          setOriginalUserDetails,
          userDetails,
          setUserDetails,
          hasCheckedAuth,
        }),
        [
          refreshUserDetails,
          setRefreshUserDetails,
          originalUserDetails,
          setOriginalUserDetails,
          userDetails,
          setUserDetails,
          hasCheckedAuth,
        ],
      )}
    >
      {children}
    </LoggedInUserContext.Provider>
  );
};

LoggedInUserContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useLoggedInUserContextProvider = () =>
  useContext(LoggedInUserContext);
